import { createClient, useClient } from '@staffingengine/database-client'
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
  type AuthChangeEvent as SupabaseAuthChangeEvent,
  type Session as SupabaseSession,
} from '@supabase/supabase-js'
import { createHooks, createDebugger as createHooksDebugger, type Hookable } from 'hookable'

const SUPABASE_API_URL = import.meta.env.VITE_SUPABASE_API_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

export type AuthHookable = Hookable<AuthHooks>

type AuthHookResult<T = void> = Promise<T> | T

type AuthHookFn<P = void> = (params: P) => AuthHookResult

export interface AuthHooks {
  'sign-in': AuthHookFn<{ session: SupabaseSession | null }>
  'sign-out': AuthHookFn
  'initial-session': AuthHookFn<{ session: SupabaseSession | null }>
  'mfa-challenge-verified': AuthHookFn<{ session: SupabaseSession | null }>
  'password-recovery': AuthHookFn<{ session: SupabaseSession | null }>
  'token-refreshed': AuthHookFn<{ session: SupabaseSession | null }>
  'user-updated': AuthHookFn<{ session: SupabaseSession | null }>
}

export const authHooks = createHooks<AuthHooks>()

export const createDatabaseClient = () => {
  createClient({ url: SUPABASE_API_URL, key: SUPABASE_ANON_KEY })

  const client = useClient()

  if (import.meta.env.DEV) {
    authHooks.beforeEach((event) =>
      console.debug(`[db client] ${event.name} called with`, {
        args: event.args,
      }),
    )
    authHooks.afterEach((event) => console.debug(`[db client] ${event.name} finished`))
    createHooksDebugger(authHooks, { tag: 'db client - hook' })
  }

  // TODO: export dedicated functions for each hook
  // onSignIn((callback) => authHooks.hook('sign-in', ({ session }) => callback(session)))

  // TODO: this should be in the auth composable, but allow registering hooks for different events from anywhere
  const authEventHandler = async (
    event: SupabaseAuthChangeEvent,
    session: SupabaseSession | null,
  ) => {
    if (import.meta.env.DEV) {
      console.log(`AUTH - Event: ${event}`)
    }

    switch (event) {
      case 'SIGNED_IN':
        await authHooks.callHook('sign-in', { session })
        break
      case 'SIGNED_OUT':
        await authHooks.callHook('sign-out')
        break
      case 'INITIAL_SESSION':
        await authHooks.callHook('initial-session', { session })
        break
      case 'MFA_CHALLENGE_VERIFIED':
        await authHooks.callHook('mfa-challenge-verified', { session })
        break
      case 'PASSWORD_RECOVERY':
        await authHooks.callHook('password-recovery', { session })
        break
      case 'TOKEN_REFRESHED':
        await authHooks.callHook('token-refreshed', { session })
        break
      case 'USER_UPDATED':
        await authHooks.callHook('user-updated', { session })
        break
      default:
        break
    }
  }

  client.supabase.auth.onAuthStateChange((event, session) => {
    /**
     * Workaround due to https://github.com/supabase/gotrue-js/issues/762#issuecomment-1780006492
     *
     * We need to ensure the supabase client isn't used while the gotrue auth still has a lock on the
     * internal node-fetch library it uses to make requests. Placing the actual processing of the event
     * into another thread solves this.
     */
    const _event = ref<SupabaseAuthChangeEvent>()

    watch(
      () => _event.value,
      (val) => {
        if (!val) return
        authEventHandler(val, session)
      },
    )

    _event.value = event
  })
}

export const useDatabaseClient = useClient

interface UseRequestOptions extends RequestInit {
  params?: { [key: string]: string | string[] | number | number[] | boolean }
  allowAnon?: boolean
}

/**
 * Make an API request to a supabase edge function
 *
 * @param route edge function path, starting with the function name and no `/`
 * @param options
 *
 * @example
 * ```ts
 * const result = await useRequest('admin/signup/user', {
 *   body: JSON.stringify({
 *     email: 'whatever@example.com',
 *   })
 * })
 * ```
 *
 * @example
 * ```ts
 * const result = await useRequest('something', {
 *   params: {
 *     id: 32,
 *     name: 'whatever',
 *   },
 *   headers: {
 *     'Se-Lol': 'nope',
 *   },
 * })
 * ```
 */
export const useRequest = async (route: string, options: UseRequestOptions) => {
  const BASE_URL = `${import.meta.env.VITE_SUPABASE_API_URL}`

  let url = `${BASE_URL}/functions/v1/${route}`

  if (options.params) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(options.params)) {
      params.append(key, value.toString())
    }

    url += `?${params.toString()}`
  }

  const token = await getAuthToken(options.allowAnon)
  if (!token) throw new Error('User session token not found')

  return await fetch(url, {
    method: options.method,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
    body: options.body,
    redirect: options.redirect || 'follow',
  })
}

/**
 * Make a request to a http endpoint on the signed in user's onereach account.
 *
 * @param route onereach flow route, minus the account id
 * @param options
 *
 * @example
 * ```ts
 * const response = await useOnereach('enginegpt/submit', {
 *   body: JSON.stringify({
 *     type: 'job-description',
 *     action: 'confirm',
 *     params: { ... },
 *   })
 * })
 * ```
 */
export const useOnereach = async (route: string, options: UseRequestOptions) =>
  await useRequest(`onereach/proxy/http/${route}`, options)

/**
 * Get the access token for the signed in user
 *
 * @returns session access token
 */
const getAuthToken = async (allowAnon?: boolean) => {
  const client = useClient()

  const {
    data: { session },
  } = await client.supabase.auth.getSession()
  if (!session?.access_token && allowAnon) {
    // @ts-expect-error TS2445 (this is protected, we don't care)
    return client.supabase.auth.headers.apikey || undefined
  }

  return session?.access_token
}

export type APIResponse<D, M = void> = {
  ok: boolean
  data: D
  warning?: string[]
  error?: string[]
  meta?: M
}
